import { AUTH_STORAGE_KEYS } from "../config/auth";
import type { EffectiveAccess, OktaVerifiedUser } from "./authService";

export type AuthSessionInput = {
  readonly accessToken: string;
  readonly idToken: string;
  readonly expiresAt: number | null;
  readonly user: OktaVerifiedUser;
  readonly effectiveAccess: EffectiveAccess;
  readonly matchedGroups: readonly string[];
};

/**
 * Persist auth across tabs/windows. `sessionStorage` is per-tab and forces re-login
 * when opening deep links in a new tab (e.g. from email).
 */
const AUTH_STORAGE: Storage = localStorage;

function toPositiveNumber(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function setAuthSession(session: AuthSessionInput): void {
  AUTH_STORAGE.setItem(AUTH_STORAGE_KEYS.accessToken, session.accessToken);
  AUTH_STORAGE.setItem(AUTH_STORAGE_KEYS.idToken, session.idToken);
  if (session.expiresAt) {
    AUTH_STORAGE.setItem(AUTH_STORAGE_KEYS.tokenExpiresAt, String(session.expiresAt));
  } else {
    AUTH_STORAGE.removeItem(AUTH_STORAGE_KEYS.tokenExpiresAt);
  }
  AUTH_STORAGE.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(session.user));
  AUTH_STORAGE.setItem(
    AUTH_STORAGE_KEYS.effectiveAccess,
    JSON.stringify(session.effectiveAccess),
  );
  AUTH_STORAGE.setItem(
    AUTH_STORAGE_KEYS.matchedGroups,
    JSON.stringify([...session.matchedGroups]),
  );
}

export function getAccessToken(): string | null {
  const token = AUTH_STORAGE.getItem(AUTH_STORAGE_KEYS.accessToken);
  return token && token.trim().length > 0 ? token : null;
}

export function getIdToken(): string | null {
  const token = AUTH_STORAGE.getItem(AUTH_STORAGE_KEYS.idToken);
  return token && token.trim().length > 0 ? token : null;
}

export function getTokenExpiry(): number | null {
  return toPositiveNumber(AUTH_STORAGE.getItem(AUTH_STORAGE_KEYS.tokenExpiresAt));
}

export function isSessionExpired(): boolean {
  const expiresAt = getTokenExpiry();
  if (!expiresAt) return false;
  return Date.now() >= expiresAt;
}

export function isAuthenticated(): boolean {
  const token = getAccessToken() ?? getIdToken();
  if (!token) return false;
  return !isSessionExpired();
}

export function getAuthUser(): OktaVerifiedUser | null {
  const raw = AUTH_STORAGE.getItem(AUTH_STORAGE_KEYS.user);
  if (!raw?.trim()) return null;
  try {
    return JSON.parse(raw) as OktaVerifiedUser;
  } catch {
    return null;
  }
}

export function getEffectiveAccess(): EffectiveAccess | null {
  const raw = AUTH_STORAGE.getItem(AUTH_STORAGE_KEYS.effectiveAccess);
  if (!raw?.trim()) return null;
  try {
    return JSON.parse(raw) as EffectiveAccess;
  } catch {
    return null;
  }
}

export function getMatchedGroups(): readonly string[] {
  const raw = AUTH_STORAGE.getItem(AUTH_STORAGE_KEYS.matchedGroups);
  if (!raw?.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  } catch {
    return [];
  }
}

export function clearAuthSession(): void {
  AUTH_STORAGE.removeItem(AUTH_STORAGE_KEYS.accessToken);
  AUTH_STORAGE.removeItem(AUTH_STORAGE_KEYS.idToken);
  AUTH_STORAGE.removeItem(AUTH_STORAGE_KEYS.tokenExpiresAt);
  AUTH_STORAGE.removeItem(AUTH_STORAGE_KEYS.user);
  AUTH_STORAGE.removeItem(AUTH_STORAGE_KEYS.effectiveAccess);
  AUTH_STORAGE.removeItem(AUTH_STORAGE_KEYS.matchedGroups);
}
